// Admin: the blåtur invitation list, absences and strikes, readmission applications and e-mail to everyone invited

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { FiMail, FiCopy, FiUserPlus, FiX } from 'react-icons/fi';
import { app } from './firebase-config';
import { BLATUR_TYPE, tripYear, tripTitle } from './blaturData';
import { normalizeName, createPerson } from './adminData';
import { NamePicker } from './AdminEvents';
import {
  STRIKES_TO_OUT, memberStatus, historicalAbsences, loadMembers, addMembers, removeMember,
  registerAbsence, registerAbsences, removeAbsence, readmit, loadApplications, decideApplication, loadEmailsByName,
} from './blaturMembers';
import { strikeEmail, outEmail, mailtoLink } from './blaturEmails';

const db = getFirestore(app);

function AbsenceForm({ defaultYear, onSave, onCancel }) {
  const [year, setYear] = useState(defaultYear);
  const [reason, setReason] = useState('');
  const [strike, setStrike] = useState(false);
  return (
    <div className="admin-inline-form">
      <input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="year" />
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Årsak (hør den før dere avgjør)" />
      <label><input type="checkbox" checked={strike} onChange={(e) => setStrike(e.target.checked)} /> Strike</label>
      <button
        className="button-17"
        disabled={!reason.trim()}
        onClick={() => onSave({ year, reason, strike })}
      >Lagre</button>
      <button className="admin-btn" onClick={onCancel}>Avbryt</button>
    </div>
  );
}

function AdminMembers({ user, roles, directory, reloadDirectory }) {
  const year = roles.config?.year || new Date().getFullYear();
  const [members, setMembers] = useState(null);
  const [applications, setApplications] = useState([]);
  const [emails, setEmails] = useState({});
  const [trips, setTrips] = useState([]);
  const [pending, setPending] = useState([]);
  const [absenceFor, setAbsenceFor] = useState(null);
  const [penalties, setPenalties] = useState({});
  const [status, setStatus] = useState('');
  const [historyYears, setHistoryYears] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    const [m, a, e, t] = await Promise.all([
      loadMembers(),
      loadApplications(),
      loadEmailsByName(directory).catch(() => ({})),
      getDocs(query(collection(db, 'events'), where('event_type', '==', BLATUR_TYPE))),
    ]);
    setMembers(m);
    setApplications(a);
    setEmails(e);
    setTrips(t.docs.map(d => d.data()));
  }, [directory]);

  useEffect(() => {
    load().catch(err => {
      console.error('Error loading invitation list: ', err);
      setLoadError(err);
    });
  }, [load]);

  const lastTrip = useMemo(() => {
    const last = {};
    trips.forEach(trip => {
      [...(trip.Deltagelse || []), ...(trip.Vert || [])].forEach(n => {
        const key = normalizeName(n);
        last[key] = Math.max(last[key] || 0, tripYear(trip));
      });
    });
    return last;
  }, [trips]);

  // Years blåtur was held, plus the coming trip, so "in a row" skips years without a trip
  const tripYears = useMemo(() => [...new Set([...trips.map(tripYear), year])].sort((a, b) => a - b), [trips, year]);
  const latestTwo = useMemo(() => trips.map(tripYear).filter(y => y < year).sort((a, b) => b - a).slice(0, 2), [trips, year]);

  const pastParticipants = useMemo(() => {
    const names = new Map();
    trips.forEach(t => [...(t.Deltagelse || []), ...(t.Vert || [])].forEach(n => {
      if (n.trim()) names.set(normalizeName(n), n.trim());
    }));
    return [...names.values()];
  }, [trips]);

  if (loadError) {
    return (
      <p className="admin-hint">
        Kunne ikke hente invitasjonslisten: {loadError.message}
        {loadError.code === 'permission-denied' && ' Deploy sikkerhetsreglene (firestore.rules) til Firebase.'}
      </p>
    );
  }
  if (!members) return <p className="admin-hint">Laster…</p>;

  const rows = members.map(m => ({ ...m, status: memberStatus(m, tripYears), ...memberStatus(m, tripYears), email: emails[normalizeName(m.name)] }));
  const invited = rows.filter(r => !r.out);
  const out = rows.filter(r => r.out);
  const invitedEmails = invited.map(r => r.email).filter(Boolean);
  const missingEmail = invited.filter(r => !r.email).map(r => r.name);
  const memberNames = new Set(members.map(m => normalizeName(m.name)));
  const pendingApplications = applications.filter(a => a.status === 'pending');

  const run = async (message, action) => {
    try {
      setStatus(message);
      await action();
      await load();
      setStatus('');
    } catch (error) {
      console.error(error);
      setStatus('Noe gikk galt: ' + error.message);
    }
  };

  // Start practising the rule on trips that already happened: absences there become strikes
  const selectedHistoryYears = historyYears || latestTwo;
  const historyPreview = historicalAbsences(members, trips, selectedHistoryYears);
  const tripByYear = new Map(trips.map(t => [tripYear(t), t]));
  const registerHistory = () => {
    const names = [...new Set(historyPreview.map(h => h.member.name.trim()))];
    if (!window.confirm(`Registrere ${historyPreview.length} strikes for ${names.length} personer? De kan fjernes enkeltvis etterpå.`)) return;
    run('Registrerer…', () => registerAbsences(historyPreview.map(h => ({
      member: h.member, year: h.year, strike: true, reason: `Uteblitt fra blåtur ${h.year} (${tripTitle(h.trip)})`,
    })), user.uid));
  };

  const organizerNames = (roles.config?.organizers || [])
    .map(uid => directory.users.find(u => u.id === uid)?.name?.trim()).filter(Boolean);

  const notify = (member) => {
    const { subject, body } = strikeEmail({
      member, status: member.status, trips, year, organizers: organizerNames, origin: window.location.origin,
    });
    window.location.href = mailtoLink({ to: member.email ? [member.email] : [], subject, body });
  };

  const mailOut = () => {
    const outEmails = out.map(r => r.email).filter(Boolean);
    const missing = out.filter(r => !r.email).map(r => r.name.trim());
    if (missing.length && !window.confirm(`Mangler e-post for ${missing.join(', ')}. De må legges til manuelt i e-posten. Fortsette?`)) return;
    const { subject, body } = outEmail({ year, organizers: organizerNames, origin: window.location.origin });
    window.location.href = mailtoLink({ bcc: outEmails, subject, body });
  };

  const mailAll = () => {
    const subject = encodeURIComponent(`Blåtur ${year}`);
    window.location.href = `mailto:?bcc=${invitedEmails.join(',')}&subject=${subject}`;
  };

  const importPast = () => {
    const names = pastParticipants.filter(n => !memberNames.has(normalizeName(n)));
    if (!window.confirm(`Legge til ${names.length} personer som har vært med på blåtur før?`)) return;
    run('Legger til…', () => addMembers(names, user.uid));
  };

  const addPending = () => {
    const names = pending.filter(n => !memberNames.has(normalizeName(n)));
    run('Legger til…', async () => { await addMembers(names, user.uid); setPending([]); });
  };

  const onCreatePerson = async (name) => {
    await createPerson({ name }, user.uid);
    await reloadDirectory();
  };

  const readmitMember = (member) => {
    const penalty = window.prompt(`Straff for å ta ${member.name} inn igjen:`);
    if (penalty === null) return;
    run('Lagrer…', () => readmit(member, { penalty }, user.uid));
  };

  const decide = (application, approved) => {
    const penalty = penalties[application.id] || '';
    if (approved && !penalty.trim() && !window.confirm('Godkjenne uten straff?')) return;
    run('Lagrer…', () => decideApplication(application, { approved, penalty }, user.uid, members));
  };

  return (
    <div>
      <section className="admin-form">
        <h2>Invitasjonsliste for blåtur {year}</h2>
        <p>
          <strong>{invited.length}</strong> inviteres{out.length > 0 && <>, <strong>{out.length}</strong> er ute etter {STRIKES_TO_OUT} strikes</>}.
        </p>
        <p className="admin-hint">
          Årets arrangører hører årsaken og avgjør om fravær er en strike. Med strikes på {STRIKES_TO_OUT} turer på rad
          er man ute til en søknad er godkjent. En tur man deltok på, eller et gyldig fravær, bryter rekken.
        </p>
        <div className="admin-actions">
          <button className="button-17" onClick={mailAll} disabled={invitedEmails.length === 0}><FiMail /> E-post til alle inviterte</button>
          <button className="admin-btn" onClick={() => navigator.clipboard?.writeText(invitedEmails.join(', '))} disabled={invitedEmails.length === 0}>
            <FiCopy /> Kopier {invitedEmails.length} adresser
          </button>
          {out.length > 0 && (
            <button className="admin-btn" onClick={mailOut} title="Én felles e-post i BCC: ikke invitert, og hvordan man søker seg tilbake">
              <FiMail /> E-post til de {out.length} som er ute
            </button>
          )}
        </div>
        {missingEmail.length > 0 && (
          <p className="admin-hint">
            Mangler e-post: {missingEmail.join(', ')}. Adressen lagres når de logger inn i appen, eller legges inn når en ny person opprettes under Personer.
          </p>
        )}
        {status && <p className="admin-status">{status}</p>}
      </section>

      {pendingApplications.length > 0 && (
        <section className="admin-form">
          <h2>Søknader om å komme inn igjen</h2>
          {pendingApplications.map(a => (
            <div key={a.id} className="admin-application">
              <p><strong>{a.name}</strong>: «{a.text}»</p>
              <div className="admin-inline-form">
                <input
                  value={penalties[a.id] || ''}
                  onChange={(e) => setPenalties(p => ({ ...p, [a.id]: e.target.value }))}
                  placeholder="Straff"
                />
                <button className="button-17" onClick={() => decide(a, true)}>Godkjenn</button>
                <button className="admin-btn" onClick={() => decide(a, false)}>Avslå</button>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="admin-form">
        <h2>Legg til på listen</h2>
        {members.length === 0 && pastParticipants.length > 0 && (
          <p>
            <button className="admin-btn" onClick={importPast}><FiUserPlus /> Legg til alle som har vært med på blåtur ({pastParticipants.length})</button>
          </p>
        )}
        <NamePicker label="Nye på listen" value={pending} onChange={setPending} names={directory.names} onCreatePerson={onCreatePerson} />
        {pending.length > 0 && (
          <div className="admin-actions">
            <button className="button-17" onClick={addPending}>Legg til {pending.length} på invitasjonslisten</button>
          </div>
        )}
      </section>

      {members.length > 0 && (
        <section className="admin-form">
          <h2>Start regelen på tidligere turer</h2>
          <p className="admin-hint">
            Registrerer strike for alle på listen som uteble fra de valgte turene. Det gjelder bare dem som hadde vært med på en
            blåtur før den aktuelle turen, og ikke dem som allerede har fravær registrert det året. Hver strike kan fjernes etterpå,
            for eksempel hvis noen hadde en god grunn.
          </p>
          <div className="admin-checklist">
            {[...trips].sort((a, b) => tripYear(b) - tripYear(a)).filter(t => tripYear(t) < year).map(t => {
              const y = tripYear(t);
              const checked = selectedHistoryYears.includes(y);
              return (
                <label key={y} className={checked ? 'checked' : ''}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setHistoryYears(checked ? selectedHistoryYears.filter(x => x !== y) : [...selectedHistoryYears, y])}
                  />
                  {y} {tripTitle(t)}
                </label>
              );
            })}
          </div>
          {historyPreview.length > 0 ? (
            <>
              <p className="admin-hint">
                Får strike: {[...selectedHistoryYears].sort().map(y => {
                  const names = historyPreview.filter(h => h.year === y).map(h => h.member.name.trim());
                  return names.length ? `${y} (${tripTitle(tripByYear.get(y))}): ${names.join(', ')}` : null;
                }).filter(Boolean).join('. ')}.
              </p>
              <button className="button-17" onClick={registerHistory}>Registrer {historyPreview.length} strikes</button>
            </>
          ) : (
            <p className="admin-hint">Ingen nye strikes å registrere for de valgte turene.</p>
          )}
        </section>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Navn</th><th>Status</th><th>Sist med</th><th>Fravær</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(m => (
              <React.Fragment key={m.id}>
                <tr className="static">
                  <td>{m.name}{!m.email && <span className="admin-hint" title="Mangler e-post"> ✉︎?</span>}</td>
                  <td>
                    {m.out ? <span className="admin-badge out">Ute</span> : <span className="admin-badge">Invitert</span>}
                    {m.strikes > 0 && !m.out && <span className="admin-badge strike">{m.inARow > 0 ? `${m.inARow} på rad` : `${m.strikes} strike`}</span>}
                  </td>
                  <td>{lastTrip[normalizeName(m.name)] || '–'}</td>
                  <td>
                    {(m.absences || []).map((a, i) => (
                      <span key={a.at} className={`admin-chip${a.strike ? ' strike' : ''}`} title={a.reason}>
                        {a.year}{a.strike ? ' strike' : ''}: {a.reason}
                        <button
                          onClick={() => window.confirm('Fjerne denne registreringen?') && run('Lagrer…', () => removeAbsence(m, i))}
                          aria-label="Fjern"
                        ><FiX /></button>
                      </span>
                    ))}
                    {(m.readmissions || []).map(r => (
                      <span key={r.at} className="admin-chip" title="Tatt inn igjen">Tatt inn igjen{r.penalty ? `: ${r.penalty}` : ''}</span>
                    ))}
                  </td>
                  <td className="admin-cell-actions">
                    <button className="admin-btn" onClick={() => setAbsenceFor(absenceFor === m.id ? null : m.id)}>Fravær</button>
                    {m.strikes > 0 && (
                      <button className="admin-btn" onClick={() => notify(m)} title="Åpner e-post med begrunnelse og hvordan man søker">
                        <FiMail /> Varsle
                      </button>
                    )}
                    {m.out && <button className="admin-btn" onClick={() => readmitMember(m)}>Ta inn igjen</button>}
                    <button
                      className="admin-btn"
                      onClick={() => window.confirm(`Fjerne ${m.name} fra invitasjonslisten?`) && run('Fjerner…', () => removeMember(m))}
                    >Fjern</button>
                  </td>
                </tr>
                {absenceFor === m.id && (
                  <tr className="static">
                    <td colSpan={5}>
                      <AbsenceForm
                        defaultYear={year}
                        onCancel={() => setAbsenceFor(null)}
                        onSave={(absence) => run('Lagrer…', async () => {
                          await registerAbsence(m, absence, user.uid);
                          setAbsenceFor(null);
                        })}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminMembers;
